import React, { useState, useEffect, useCallback } from 'react';
import {
  AppBar,
  Toolbar,
  IconButton,
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Menu,
  MenuItem,
  Divider,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Badge,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Search as SearchIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  ShoppingBasket as ShoppingBasketIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { API_BASE_URL } from './config.js';
import { getCurrentQuotation, QUOTATION_UPDATED_EVENT } from './quotationApi.js';

const Header = () => {
  const [accountMenuAnchor, setAccountMenuAnchor] = useState(null);
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [quotationCount, setQuotationCount] = useState(0);
  const [currentQuotationId, setCurrentQuotationId] = useState(null);

  const refreshQuotationCount = useCallback(async () => {
    if (!localStorage.getItem('access')) return;
    try {
      const quotation = await getCurrentQuotation();
      // Badge shows the number of distinct line items in the basket, not the
      // summed quantity across them (that's what `item_count` from the API is).
      setQuotationCount(quotation.line_items?.length || 0);
      setCurrentQuotationId(quotation.id);
    } catch (error) {
      // Not fatal to the header - just leave the badge as-is.
    }
  }, []);

  useEffect(() => {
    refreshQuotationCount();
  }, [refreshQuotationCount, location.pathname]);

  useEffect(() => {
    window.addEventListener(QUOTATION_UPDATED_EVENT, refreshQuotationCount);
    return () => window.removeEventListener(QUOTATION_UPDATED_EVENT, refreshQuotationCount);
  }, [refreshQuotationCount]);

  const handleAccountMenuOpen = (event) => {
    setAccountMenuAnchor(event.currentTarget);
  };

  const handleAccountMenuClose = () => {
    setAccountMenuAnchor(null);
  };

  const handleMobileDrawerOpen = () => {
    setMobileDrawerOpen(true);
  };

  const handleMobileDrawerClose = () => {
    setMobileDrawerOpen(false);
  };

  const handleSearch = () => {
    if (searchQuery.trim()) {
      navigate(`/products/list?search=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/products');
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      handleSearch();
    }
  };

  const handleNavigation = (path) => {
    handleAccountMenuClose();
    handleMobileDrawerClose();
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      // Close menu first
      handleAccountMenuClose();

      const refreshToken = localStorage.getItem('refreshToken');

      // Make API call without Authorization
      await fetch(`${API_BASE_URL}/api/logout/`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh_token: refreshToken }), // optional
      });

    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear local storage
      localStorage.clear();
      sessionStorage.clear();
      navigate('/');
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Top Banner */}
      <Box
        sx={{
          backgroundColor: '#f9a91e',
          color: '#000',
          padding: '8px 0',
          textAlign: 'center',
          fontSize: '14px',
          fontWeight: 500,
        }}
      >
        Welcome!
      </Box>

      {/* Main Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: '#fff', 
          color: '#000',
          boxShadow: 'none',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar sx={{ 
            minHeight: { xs: '60px !important', md: '80px !important' },
            justifyContent: 'space-between',
            padding: '0 !important'
          }}>
            {/* Left Section - Logo and Mobile Menu */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>

              {/* Logo */}
              <Box
                component="img"
                src="//gibsonhomewares.com/cdn/shop/files/GibsonHomewares_Logo_140x@2x.png"
                alt="Gibson Homewares"
                sx={{
                  height: { xs: 30, md: 40 },
                  width: 'auto',
                  maxWidth: 140,
                  cursor: 'pointer',
                  '&:hover': {
                    opacity: 0.8,
                  },
                }}
                onClick={() => navigate('/home')}
              />

              
            </Box>

            {/* Right Section - Title and Account */}
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: { xs: 2, md: 4 }
            }}>
              {/* Sales Rep Portal Title */}
              <Typography 
                variant="h6"
                component="h2"
                sx={{ 
                  color: 'primary.main',
                  fontSize: { 
                    xs: '1rem', 
                    sm: '1.1rem', 
                    md: '1.25rem' 
                  },
                  fontWeight: 600,
                  whiteSpace: 'nowrap',
                }}
              >
                GIBSON SALES PORTAL
              </Typography>

              {/* Quotations (Quote Basket) */}
              <IconButton
                color="inherit"
                onClick={() => navigate(currentQuotationId ? `/quotations/${currentQuotationId}` : '/quotations')}
                sx={{ color: '#000' }}
                aria-label="quotations"
              >
                <Badge badgeContent={quotationCount} color="primary">
                  <ShoppingBasketIcon />
                </Badge>
              </IconButton>

              {/* Account Icon */}
              <IconButton
                color="inherit"
                onClick={handleAccountMenuOpen}
                sx={{ color: '#000' }}
              >
                <PersonIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </Container>

        {/* Account Menu (Desktop) */}
        <Menu
          anchorEl={accountMenuAnchor}
          open={Boolean(accountMenuAnchor)}
          onClose={handleAccountMenuClose}
          sx={{
            '& .MuiPaper-root': {
              marginTop: '8px',
              borderRadius: 0,
              boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
            }
          }}
        >
          <MenuItem 
            onClick={() => handleNavigation('/my-account')} 
            sx={{ fontSize: '14px', padding: '12px 20px' }}
          >
            My Account
          </MenuItem>
          <MenuItem 
            onClick={() => handleNavigation('/catalogs')} 
            sx={{ fontSize: '14px', padding: '12px 20px' }}
          >
            Catalogs
          </MenuItem>
          <MenuItem 
            onClick={() => handleNavigation('/products/list')} 
            sx={{ fontSize: '14px', padding: '12px 20px' }}
          >
            Products
          </MenuItem>
          <MenuItem 
            onClick={() => handleNavigation('/marketing')} 
            sx={{ fontSize: '14px', padding: '12px 20px' }}
          >
            Brands
          </MenuItem>
          <Divider />
          <MenuItem 
            onClick={handleLogout} 
            sx={{ fontSize: '14px', padding: '12px 20px' }}
          >
            Logout
          </MenuItem>
        </Menu>
      </AppBar>
    </Box>
  );
};

export default Header;